import React, { useState, useMemo } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import qs from 'qs';
import { isEmpty, pick } from 'lodash-es';

import skeletons from './Skeletons';
import ForecastsTable from './ForecastsTable';
import ForecastsSidebar from './ForecastsSidebar';
import KpiForecastChart from './KpiForecastChart';
import ForecastsChartLayout from './ForecastsChartLayout';
import { ForecastsCompanyHeader } from './ForecastsHeader';
import Button from '../../../../../components/Buttons/Button';
import SimpleDropdown from '../../../../../components/Dropdowns/SimpleDropdown';
import ConsensusSourceText from '../../../../../components/ConsensusSourceText';

const Forecasts = ({
  forecasts,
  pending,
  activeTab,
  activeQuarter,
  quartersOptions,
  availableKpis,
  className,
  makeScreenshotUrl,
  onQuarterChange,
  onActiveForecastChange,
}) => {
  const [view, setView] = useState('values');

  const activeForecast = useMemo(
    () =>
      activeTab ? forecasts?.find((forecast) => forecast?.kpi_name === activeTab) : forecasts?.[0],
    [forecasts, activeTab]
  );

  const {
    series,
    data_pack,
    bloomberg_ticker,
    forecast_period,
    forecast_published_at,
    forecast_index,
    final_forecast,
  } = activeForecast?.forecasts || {};

  const activeSeries = !isEmpty(series)
    ? { ...pick(series, ['categories', 'published']), ...series[view] }
    : {};

  const handleTabChange = (tab) => {
    onActiveForecastChange(tab);
  };

  const exportParams = {
    bloomberg_ticker,
    date: data_pack?.date,
    email_id: data_pack?.email_id,
    fy_quarter: forecast_period,
    forecast_published_at,
    forecast_index,
    final_forecast,
  };

  const downloadButton = data_pack?.exists && (
    <Button
      variant="secondary"
      disabled={pending}
      href={`/kpi_forecasts/export.zip?${qs.stringify(exportParams)}`}
    >
      Download
      <i className="hicon-download-thin" />
    </Button>
  );

  const quartersDropdown = onQuarterChange && (
    <SimpleDropdown
      selected={activeQuarter}
      options={quartersOptions}
      variant="light"
      onChange={onQuarterChange}
    />
  );

  return (
    <div className={cx('card-box forecasts', className)}>
      <ForecastsCompanyHeader
        pending={pending}
        activeTab={activeTab}
        forecasts={forecasts}
        availableKpis={availableKpis}
        quartersDropdown={quartersDropdown}
        downloadButton={downloadButton}
        onTabChange={handleTabChange}
      />

      <div className="card-box__body">
        <div className="toggle-block">
          <div className="toggle-block-box">
            <div className="forecasts-main b-t">
              <ForecastsSidebar
                title="Forecast"
                activeForecast={activeForecast}
                pending={pending}
              />
              <div className="forecasts-main-content">
                <ForecastsChartLayout
                  view={view}
                  title="History"
                  subtitle={
                    pending
                      ? skeletons.forecastSubtitle
                      : activeForecast?.forecasts?.magnitude
                      ? `${activeForecast.forecasts.kpi_metric_name} (${activeForecast.forecasts.magnitude})`
                      : ''
                  }
                  disabled={pending}
                  screenshotUrl={
                    makeScreenshotUrl &&
                    makeScreenshotUrl({
                      view,
                      kpi: activeForecast?.kpi_name,
                      va_kpi_id: activeForecast?.kpi_id,
                      fy_quarter: activeQuarter,
                      ticker: bloomberg_ticker,
                    })
                  }
                  showSourceText
                  setView={setView}
                >
                  {pending ? (
                    skeletons.forecastsMainChart(193)
                  ) : (
                    <KpiForecastChart
                      view={view}
                      series={activeSeries}
                      chartHeight={171}
                      showSourceText={false}
                    />
                  )}
                </ForecastsChartLayout>
                <div className="forecasts-main-content__table">
                  {pending ? (
                    skeletons.forecastsMainTable(false)
                  ) : (
                    <ForecastsTable activeForecast={activeForecast} />
                  )}
                </div>
                <div className="forecasts-main-content__footer">
                  <div className="highcharts-watermark" />
                  <ConsensusSourceText className="forecasts-main-content__footer-description" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Forecasts.propTypes = {
  forecasts: T.array,
  activeTab: T.string,
  pending: T.bool,
  quartersOptions: T.array,
  activeQuarter: T.string,
  availableKpis: T.array,
  className: T.string,
  makeScreenshotUrl: T.func,
  onQuarterChange: T.func,
  onActiveForecastChange: T.func.isRequired,
};

export default Forecasts;
